import { Globe } from "@phosphor-icons/react";
import { useBranding } from "@/contexts/BrandingContext";

const ASSET_BASE = process.env.REACT_APP_BACKEND_URL || "";

function resolveLogoUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${ASSET_BASE}/${u.replace(/^\/+/, "")}`;
}

/**
 * BrandLogo - unified brand mark used everywhere in the app.
 *
 * Renders:
 *   • uploaded logo image (if admin has uploaded one) OR
 *   • a green circular badge with the Globe icon (fallback)
 *
 * Props:
 *   size          - pixel size of the mark (default 36)
 *   iconSize      - size of the fallback Globe icon (default 20)
 *   showName      - whether to append the site name next to the mark
 *   nameClassName - Tailwind classes for the site name text
 *   className     - Tailwind classes for the outer wrapper
 *   markClassName - overrides the fallback badge background
 */
export default function BrandLogo({
  size = 36,
  iconSize = 20,
  showName = true,
  nameClassName = "font-heading text-lg font-semibold text-white tracking-tight",
  className = "flex items-center gap-2.5",
  markClassName = "bg-emerald-700",
}) {
  const { branding } = useBranding();
  const logo = resolveLogoUrl(branding?.logo_url);
  const name = branding?.site_name || "Conexus Foundation";

  return (
    <div className={className} data-testid="brand-logo">
      {logo ? (
        <img
          src={logo}
          alt={name}
          style={{ width: size, height: size }}
          className="object-contain rounded-md"
        />
      ) : (
        <div
          style={{ width: size, height: size }}
          className={`rounded-full flex items-center justify-center ${markClassName}`}
        >
          <Globe size={iconSize} weight="bold" className="text-white" />
        </div>
      )}
      {showName && <span className={nameClassName}>{name}</span>}
    </div>
  );
}
